import React, { useState, useEffect, useRef } from 'react';
import { Chart, Pie } from 'react-chartjs-2';
import { Bar } from 'react-chartjs-2';
import PercentIcon from '@mui/icons-material/Percent';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartOptions, ChartData, PointElement, LineElement, LineController, BarController } from 'chart.js';
import Slider from '@mui/material/Slider';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import EMIPDFGenerator from '../pdf/pdf-emi';
import ReactGA from 'react-ga4'
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import { saveEMI, updateEMI } from '../Database/emi';
import { EMI } from '../models/emi';
import { IconButton, Typography } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrencySymbol } from '../Redux/currencySlice';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SavedSnackbar from '../utils/saved-snackbar';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, BarController, LineController, Tooltip, Legend, Title);
ReactGA.event({ 'category': 'emi calculator', 'action': 'Calculate Button', 'label': 'EMI Calculator' })

const EMICalculator: React.FC<{ emi?: EMI }> = ({ }) => {
  const [isMonthlySelected, setIsMonthlySelected] = useState(true); // Default selection can be set here
  const location = useLocation();
  const [emiData, setEmiData] = useState<EMI | null>(null);
  const [isMonths, setIsMonths] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const currencySymbol = useSelector((state: RootState) => state.country.currencySymbol);
  const [amount, setAmount] = useState('10,00,000');
  const [interest, setInterest] = useState('7.5');
  const [period, setPeriod] = useState('2');
  const [processingFee, setProcessingFee] = useState('');
  const [monthlyEMI, setMonthlyEMI] = useState<number | null>(null);
  const [principalAmount, setPrincipalAmount] = useState<number | null>(null);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);
  const [yearlyBreakdown, setYearlyBreakdown] = useState<any[]>([]);
  const [expandedYear, setExpandedYear] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [errors, setErrors] = useState({
    amount: '',
    interest: '',
    period: '',
  });
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);

  const formatNumberWithCommas = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-IN').format(numValue);
  };

  const validateInputs = () => {
    const newErrors = {
      amount: "",
      interest: "",
      period: "",
    };
    let isValid = true;

    const minprincipal = 100000;

    if (!amount) {
      newErrors.amount = " Loan amount is mandatory.";
      isValid = false;
    } else if (parseFloat(amount.replace(/,/g, "")) < minprincipal) {
      newErrors.amount = "Loan amount can't be less than " + currencySymbol + "100000.";
      isValid = false;
    }

    const rateValue = parseFloat(interest.replace(/,/g, ""));
    if (!interest) {
      newErrors.interest = "Interest rate is mandatory.";
      isValid = false;
    } else if (isNaN(rateValue) || rateValue < 1 || rateValue >= 30) {
      newErrors.interest = 'Interest rate must be between 1 and 30.';
      isValid = false;
    }

    if (!period) {
      newErrors.period = "Period unit (years or months) is mandatory.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };



  const handleReset = () => {
    ReactGA.event({ 'category': 'emi calculator', 'action': 'Emi Reset Button', 'label': 'EMI Calculator' })
    setErrors({ amount: '', interest: '', period: '' });

    setAmount('1000000 ');
    setInterest('7.5');
    setPeriod('2');
    setProcessingFee('');
    setMonthlyEMI(null);
    setTotalInterest(null);
    setYearlyBreakdown([]);
    setExpandedYear(null);
    setShowPdfButton(false);
    // handleCalculate();
  };


  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }
    const emiUpdate: EMI = {
      amount: parseFloat(amount.replace(/,/g, "")),
      interest: parseFloat(interest),
      currencySymbol,
      title: title,
      period: parseInt(period?.toString() || "0", 10),
      periodUnit: isMonths ? 'months' : 'years',


    };

    if (emiData?.id) {
      await updateEMI(emiData.id, emiUpdate);
      console.log(`emi with ID ${emiData.id} updated successfully.`);
    } else {
      await saveEMI(emiUpdate);
      console.log("New emi created successfully.");
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");

    handleDialogClose();
    setTitle("");

  };

  useEffect(() => {
    const { emi: passedEmi, currencySymbol: passedCurrencySymbol } =
      location.state || {};

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    } else {
      setSymbol(currencySymbol);
    }

    if (passedEmi) {
      setEmiData(passedEmi);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setAmount(passedEmi.amount.toString());
      setInterest(passedEmi.interest.toString());
      setPeriod(passedEmi.period.toString());
      setTitle(passedEmi.title);
      setSavedTitle(passedEmi.title || "");
      if (passedEmi.periodUnit) {
        setIsMonths(passedEmi.periodUnit === 'months');
      }
      handleCalculate()

    }

    handleCalculate()


  }, [location.state, emiData]);


  const handleCalculate = () => {
    ReactGA.event({ 'category': 'emi calculator', 'action': 'Emi Calculate Button', 'label': 'EMI Calculator' });

    if (!validateInputs()) {
      return;
    }

    const principal = parseFloat(amount.replace(/,/g, ''));
    const annualRate = parseFloat(interest.replace(/,/g, ''));
    const years = isMonths ? parseFloat(period) / 12 : parseFloat(period);

    if (isNaN(principal) || isNaN(annualRate) || isNaN(years) || principal <= 0 || annualRate <= 0 || years <= 0) {
      return;
    }

    const monthlyRate = annualRate / 12 / 100;
    const months = years * 12;
    const EMI = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalPayment = EMI * months;
    const totalInterestPaid = totalPayment - principal;

    setMonthlyEMI(EMI);
    setTotalInterest(totalInterestPaid);
    const yearlyDetails = calculateYearlyBreakdown(principal, monthlyRate, EMI, months);
    setYearlyBreakdown(yearlyDetails);
    setShowPdfButton(true);
    setPrincipalAmount(principal);
    setIsCalculated(true)
  };


  const calculateYearlyBreakdown = (principal: number, monthlyRate: number, EMI: number, months: number) => {
    let remainingBalance = principal;
    let remainingMonths = months;
    let cumulativePrincipalPaid = 0;

    const breakdown = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    let startYear = currentYear;
    let startMonth = currentMonth;
    let numOfYears = Math.ceil(months / 12);

    for (let year = 0; year <= numOfYears; year++) {
      let interestPaid = 0;
      let principalPaid = 0;
      const monthlyDetails = [];
      let numMonthsThisYear = year === 0 ? Math.min(12 - startMonth, remainingMonths) : (remainingMonths > 12 ? 12 : remainingMonths);

      for (let month = 0; month < numMonthsThisYear; month++) {
        const actualMonthIndex = (startMonth + month) % 12;
        const actualYear = startYear;

        if (remainingBalance <= 0 || remainingMonths <= 0) break;

        const interestForMonth = remainingBalance * monthlyRate;
        interestPaid += interestForMonth;
        const principalForMonth = EMI - interestForMonth;
        principalPaid += principalForMonth;
        remainingBalance -= principalForMonth;
        cumulativePrincipalPaid += principalForMonth;

        const loanPaidToDate = Math.ceil((cumulativePrincipalPaid / principal) * 100);

        monthlyDetails.push({
          monthName: monthNames[actualMonthIndex],
          year: actualYear,
          principalPaid: principalForMonth,
          interestCharged: interestForMonth,
          totalPayment: principalForMonth + interestForMonth,
          balance: Math.max(0, remainingBalance),
          loanPaidToDate: loanPaidToDate.toFixed(2),
        });
      }

      if (monthlyDetails.length > 0) {
        const loanPaidToDateYearly = Math.ceil((cumulativePrincipalPaid / principal) * 100);

        breakdown.push({
          year: startYear,
          principalPaid: principalPaid,
          interestCharged: interestPaid,
          totalPayment: principalPaid + interestPaid,
          balance: Math.max(0, remainingBalance),
          loanPaidToDate: loanPaidToDateYearly.toFixed(2),
          monthlyDetails: monthlyDetails,
        });
      }

      remainingMonths -= numMonthsThisYear;
      startYear += 1;
      startMonth = 0;
    }

    return breakdown;
  };


  const toggleYearExpansion = (yearIndex: number) => {
    setExpandedYear(expandedYear === yearIndex ? null : yearIndex);
  };

  const totalPrincipal = yearlyBreakdown.reduce((acc, yearDetail) => acc + yearDetail.principalPaid, 0);
  const calculatedTotalInterest = yearlyBreakdown.reduce((acc, yearDetail) => acc + yearDetail.interestCharged, 0);
  const calculatedTotalBalance = yearlyBreakdown.reduce((acc, yearDetail) => acc + yearDetail.balance, 0);

  const chartData = {
    labels: yearlyBreakdown.map((yearDetail) => yearDetail.year),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Principal (%)',
        data: yearlyBreakdown.map((yearDetail) =>
          Math.ceil((yearDetail.principalPaid / totalPrincipal) * 100)
        ),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        stack: 'Stack 0',
      },
      {
        type: 'bar' as const,
        label: 'Interest (%)',
        data: yearlyBreakdown.map((yearDetail) =>
          Math.ceil((yearDetail.interestCharged / calculatedTotalInterest) * 100)
        ),
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        stack: 'Stack 0',
      },
      {
        type: 'line' as const,
        label: 'Balance (%)',
        data: yearlyBreakdown.map((yearDetail) =>
          Math.ceil((yearDetail.balance / calculatedTotalBalance) * 100)
        ),
        borderColor: 'rgba(255, 99, 132, 0.6)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        fill: false,
        tension: 0.1,
      },
    ],
  };

  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
    isDecimalAllowed: boolean = false,
    maxValue: number | null = null,
    decimalPlaces: number = 2
  ) => {
    let sanitizedValue = isDecimalAllowed
      ? value.replace(/[^0-9.]/g, '').replace(/(\..*)\..*/g, '$1')
      : value.replace(/[^0-9]/g, '');

    const numericValue = sanitizedValue ? parseFloat(sanitizedValue) : null;

    if (value.length > 0 && value[0] === '0' && value !== '0') {
      return;
    }
    if (value === "0") {
      return ("");
    }

    if (isDecimalAllowed && numericValue !== null) {
      const parts = sanitizedValue.split('.');
      if (parts[1]?.length > decimalPlaces) {
        sanitizedValue = `${parts[0]}.${parts[1].slice(0, decimalPlaces)}`;
      }
    }

    if (maxValue !== null && numericValue !== null && numericValue > maxValue) {
      setter(formatNumberWithCommas(maxValue.toFixed(decimalPlaces)));
    } else {
      setter(sanitizedValue);
    }
  };

  const handleNumericChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxValue: number | null = null
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, '');
    const cleanedValue = rawValue.replace(/[^0-9]/g, '');

    if (rawValue.length > 1 && rawValue[0] === '0') {
      return;
    }
    if (rawValue === "0") {
      setter('');
      return;
    }

    const numericValue = cleanedValue ? parseFloat(cleanedValue) : null;
    if (maxValue !== null && numericValue !== null && numericValue > maxValue) {
      setter(formatNumberWithCommas(maxValue));
      setErrors((prevErrors) => ({
        ...prevErrors,
        period: `The maximum allowed value is ${maxValue} ${isMonths ? 'months' : 'years'}.`
      }));
    } else {

      setter(cleanedValue ? formatNumberWithCommas(Number(cleanedValue)) : '');
      setErrors((prevErrors) => ({ ...prevErrors, period: '' }));
    }
  };



  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };


  return (
    <>
      <Helmet>
        <title>EMI Calculator</title>
        <meta
          name="description"
          content="Calculate your Equated Monthly Installment (EMI) for loans using our EMI Calculator. Estimate your monthly payments easily and plan your finances better."
        />
        <link rel="canonical" href="/emi-calculator.html" />
      </Helmet>

      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />

      <div className="container mt-5" onKeyPress={handleKeyPress}>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-2" style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}>
            EMI Calculator
          </h5>
        </div>
        <p>The EMI (Equated Monthly Installment) Calculator helps you easily estimate the monthly repayment amount for loans based on the principal amount,
          interest rate, and tenure. By adjusting these variables, you can determine a suitable EMI that fits within your budget, ensuring timely repayment and financial planning.</p>
        {savedTitle && (
          <Typography variant="h6" style={{ marginLeft: '20px' }}>
            {savedTitle}
          </Typography>
        )}
        <div className="row justify-content-center ">
          <div className="container-fluid mt-3 ml-5">
            <div className="row justify-content-center">
              <div className="col-12 col-md-6 mb-4">

                <div className="card shadow-sm" style={{ width: '100%', height: '556px', margin: '0 auto' }}>
                  <div className="card-body">
                    <div className="mb-3">
                      <label htmlFor="amount" className="form-label">Loan Amount ({currencySymbol})</label>
                      <input
                        type="text"
                        className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                        id="amount"
                        value={amount}
                        onChange={handleNumericChange(setAmount, 10000000)}
                      />
                      {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                    </div>
                    <div className="mb-3 d-flex justify-content-center">
                      <Slider
                        min={100000}
                        max={10000000}
                        step={1000}
                        value={parseFloat(amount.replace(/,/g, ''))}
                        onChange={(e, newValue) => setAmount(newValue.toString())}
                        valueLabelDisplay="auto"
                        shiftStep={30}
                        sx={{ width: '95%' }}
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="interest" className="form-label">Annual Interest Rate (%)</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${errors.interest ? 'is-invalid' : ''}`}
                          id="interest"
                          value={interest}
                          onChange={(e) => handleInputChange(setInterest, e.target.value, true, 30, 2)}
                        />
                        <span className="input-group-text">
                          <PercentIcon />
                        </span>
                      </div>
                      {errors.interest && <div className="text-danger">{errors.interest}</div>}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="period" className="form-label">Loan Period</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${errors.period ? 'is-invalid' : ''}`}
                          id="period"
                          value={period}
                          onChange={handleNumericChange(setPeriod, isMonths ? 120 : 30)}
                        />
                        <button
                          type="button"
                          className={`btn ${isMonths ? 'btn-outline-secondary' : 'btn-primary'} no-border-radius`}
                          onClick={() => setIsMonths(false)}
                        >
                          Years
                        </button>
                        <button
                          type="button"
                          className={`btn ${isMonths ? 'btn-primary' : 'btn-outline-secondary'} no-border-radius`}
                          onClick={() => setIsMonths(true)}
                        >
                          Months
                        </button>
                      </div>
                      {errors.period && <div className="invalid-feedback">{errors.period}</div>}
                    </div>



                    <div className="d-flex justify-content-center " style={{ marginTop: '60px' }}>
                      <button
                        className="btn btn-primary btn-lg "
                        onClick={handleCalculate}

                      >
                        Calculate
                      </button>
                    </div>
                    <div className='d-flex justify-content-center'>
                      <a
                        onClick={handleReset}
                        style={{ cursor: "pointer", textDecoration: "none" }}
                        className=" pt-2"
                      >
                        Reset
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Section */}

              {monthlyEMI !== null && totalInterest !== null && (
                <div className="col-12 col-md-6 mb-4">
                  <div className="card shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-4">Result</h5>
                        <IconButton color="secondary" onClick={handleDialogOpen}>
                          {icon}
                        </IconButton>
                      </div>
                      <p className="mb-3">
                        Loan Amount:
                        <span className="float-end">
                          {currencySymbol} {principalAmount !== null ? `${formatNumberWithCommas(Math.ceil(principalAmount).toFixed(2))}` : ''}
                        </span>
                      </p>
                      <p className="mb-3">
                        Monthly EMI:
                        <span className="float-end">
                          {currencySymbol} {formatNumberWithCommas(Math.ceil(monthlyEMI).toFixed(2))}
                        </span>
                      </p>
                      <p className="mb-3">
                        Total Interest Payable:
                        <span className="float-end">
                          {currencySymbol} {formatNumberWithCommas(Math.ceil(totalInterest).toFixed(2))}
                        </span>
                      </p>
                    </div>
                  </div>

                  {yearlyBreakdown.length > 0 && (
                    <div className="card shadow-sm mt-4">
                      <div className="card-body">
                        <h5 className="card-title mb-4">Yearly Payment Overview</h5>
                        <div className="d-flex justify-content-center align-items-center" style={{ width: '100%', height: '250px' }}>
                          <Chart type="bar" data={chartData} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Yearly Breakdown Section */}
              {yearlyBreakdown.length > 0 && (
                <div className="col-12 col-md-12 mb-4">
                  <div className="card shadow-sm mt-4">
                    <div className="card-body">
                      <h5 className="card-title mb-4">Yearly Breakdown</h5>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'center' }}>Year</th>
                              <th style={{ textAlign: 'center' }}>Principal</th>
                              <th style={{ textAlign: 'center' }}>Interest</th>
                              <th style={{ textAlign: 'center' }}>Total Payment</th>
                              <th style={{ textAlign: 'center' }}>Balance</th>
                              <th style={{ textAlign: 'center' }}>Loan Paid To Date (%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {yearlyBreakdown.map((yearDetail, yearIndex) => (
                              <React.Fragment key={yearIndex}>
                                <tr onClick={() => toggleYearExpansion(yearIndex)} style={{ cursor: 'pointer' }}>
                                  <td style={{ textAlign: 'center' }} onClick={() => setExpandedYear(expandedYear === yearIndex ? null : yearIndex)}>
                                    {expandedYear === yearIndex ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    {yearDetail.year}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(yearDetail.principalPaid.toFixed(2))}</td>
                                  <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(yearDetail.interestCharged.toFixed(2))}</td>
                                  <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(yearDetail.totalPayment.toFixed(2))}</td>
                                  <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(yearDetail.balance.toFixed(2))}</td>
                                  <td style={{ textAlign: 'center' }}>{yearDetail.loanPaidToDate}%</td>
                                </tr>

                                {expandedYear === yearIndex
                                  ? yearDetail.monthlyDetails.map((monthDetail: any, monthIndex: number) => (
                                    <tr key={`${yearIndex}-${monthIndex}`} style={{ backgroundColor: '#DEDEDE', background: '#DEDEDE !important' }}>
                                      <td style={{ textAlign: 'center' }} className="ps-4">{monthDetail.monthName}</td>
                                      <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(monthDetail.principalPaid.toFixed(2))}</td>
                                      <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(monthDetail.interestCharged.toFixed(2))}</td>
                                      <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(monthDetail.totalPayment.toFixed(2))}</td>
                                      <td style={{ textAlign: 'center' }}>₹{formatNumberWithCommas(monthDetail.balance.toFixed(2))}</td>
                                      <td style={{ textAlign: 'center' }}>{monthDetail.loanPaidToDate}%</td>
                                    </tr>
                                  ))
                                  : null}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-end" style={{ marginTop: "40px", paddingLeft: "90px", marginBottom: '10px' }}>

                {showPdfButton && (
                  <EMIPDFGenerator
                    monthlyEMI={monthlyEMI}
                    totalInterest={totalInterest}
                    yearlyBreakdown={yearlyBreakdown}
                    amount={amount}
                    interest={interest}
                    period={period}
                    isMonths={isMonths}
                    isCalculated={isCalculated}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default EMICalculator;
